import React, { useState, useEffect, useRef, useCallback } from 'react';
import dashboardService from '../../Api/DashboardService';
import { useNavigate } from 'react-router-dom';
import { ChartDataProvider } from '@mui/x-charts/ChartDataProvider';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { toast } from 'react-toastify';
import { uploadModalProps } from '../../Const/constant';

// Define a cohesive color palette
const COLORS = {
    primary: {
        blue: '#3b82f6',      // Modern blue
        green: '#10b981',     // Modern green
        yellow: '#f59e0b',    // Modern amber/yellow
        purple: '#8b5cf6',    // Modern purple
        red: '#ef4444',       // Modern red
        cyan: '#06b6d4',      // Modern cyan
    },
    secondary: {
        blue: '#93c5fd',      // Light blue
        green: '#86efac',     // Light green
        yellow: '#fde68a',    // Light yellow
        purple: '#c4b5fd',    // Light purple
        red: '#fca5a5',       // Light red
        cyan: '#67e8f9',      // Light cyan
    },
    neutral: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827',
    }
};

const DashboardView = () => {
    const [dashboardStats, setDashboardStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportPeriod, setReportPeriod] = useState('weekly');
    const navigate = useNavigate();

    // Refs to prevent double initialization and concurrent calls
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const abortControllerRef = useRef(null);

    // Map display values to API values
    const periodMapping = {
        'Daily': 'daily',
        'Weekly': 'weekly',
        'Monthly': 'monthly'
    };

    const reversePeriodMapping = {
        'daily': 'Daily',
        'weekly': 'Weekly',
        'monthly': 'Monthly'
    };

    // Calculate progress status from API response
    const calculateProgressStatus = (stats) => {
        if (!stats?.in_progress_and_pending) return { inProgress: 0, pending: 0 };

        const {
            pdi_in_progress,
            pdi_pending,
            test_in_progress,
            test_pending,
            buildup_in_progress,
            buildup_pending
        } = stats.in_progress_and_pending;

        // Calculate totals
        const totalInProgress = pdi_in_progress + test_in_progress + buildup_in_progress;
        const totalPending = pdi_pending + test_pending + buildup_pending;

        return {
            inProgress: totalInProgress,
            pending: totalPending
        };
    };

    // Calculate ready to close status from API response
    const calculateReadyToClose = (stats) => {
        if (!stats?.pdi_flag_comparison) return { readyToClose: 0, incomplete: 0 };

        const { completed_count, in_progress_count } = stats.pdi_flag_comparison;

        return {
            readyToClose: completed_count,
            incomplete: in_progress_count
        };
    };

    // Generate chart data based on API response and selected period
    const getChartData = (stats) => {
        if (!stats?.complete_range || stats.complete_range.length === 0) {
            // Fallback data if no API data
            return getFallbackChartData();
        }

        const completeRange = stats.complete_range;

        let xAxisData = [];
        const pdiData = [];
        const buildupData = [];
        const testData = [];

        completeRange.forEach(item => {
            // Format date based on period
            let label = '';
            const rawDate = item.date;            // "2025-11-13"
            const dateObj = new Date(rawDate);    // Convert to Date object

            if (reportPeriod === "daily") {
                // Show day name: Mon, Tue, Wed...
                label = dateObj.toLocaleDateString("en-US", { weekday: "short" }) +
                    " " +
                    dateObj.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit"
                    });
            }
            else if (reportPeriod === "weekly") {
                // Show week number: Week 1, Week 2...
                label = item.week;
            }
            else if (reportPeriod === "monthly") {
                // Show month name: Jan, Feb...
                label = item.month//dateObj.toLocaleDateString("en-US", { month: "short" });
            }


            xAxisData.push(label);
            pdiData.push(item.pdi || 0);
            buildupData.push(item.buildup || 0);
            testData.push(item.test || 0);
        });

        return {
            xAxisData,
            seriesData: [

                {
                    label: uploadModalProps['buildup'],
                    type: 'bar',
                    data: buildupData,
                    color: COLORS.primary.yellow
                },
                {
                    label: uploadModalProps['test'],
                    type: 'bar',
                    data: testData,
                    color: COLORS.primary.green
                }, {
                    label: uploadModalProps['pdi'],
                    type: 'bar',
                    data: pdiData,
                    color: COLORS.primary.blue
                },
            ]
        };
    };

    // Fallback chart data when API returns no data
    const getFallbackChartData = () => {
        let xAxisData = [];
        let seriesData = [];

        if (reportPeriod === 'daily') {
            // Generate dates for the last 7 days
            const today = new Date();
            for (let i = 6; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                xAxisData.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
            }
        } else if (reportPeriod === 'weekly') {
            xAxisData = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7'];
        } else if (reportPeriod === 'monthly') {
            xAxisData = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        }

        const zeroData = new Array(xAxisData.length).fill(0);

        seriesData = [
            {
                label: uploadModalProps['buildup'],
                type: 'bar',
                data: zeroData,
                color: COLORS.primary.yellow
            },
            {
                label: uploadModalProps['test'],
                type: 'bar',
                data: zeroData,
                color: COLORS.primary.green
            },
            {
                label: uploadModalProps['pdi'],
                type: 'bar',
                data: zeroData,
                color: COLORS.primary.blue
            },

        ];

        return { xAxisData, seriesData };
    };

    // Memoized fetch function to prevent recreation on every render
    const fetchDashboardData = useCallback(async (range) => {
        // Prevent concurrent calls
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setLoading(true);
        setError(null);

        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            const statsResult = await dashboardService.getDashboardStats(range);
            setDashboardStats(statsResult);
        } catch (err) {
            // Don't show error if request was cancelled
            if (err.name === 'AbortError') return;
            toast.error("Unable to fetch Dashboard", { autoClose: 3000 });
            // Set empty stats if API fails
            setDashboardStats({
                complete_range: [],
                in_progress_and_pending: {
                    pdi_in_progress: 0,
                    pdi_pending: 0,
                    test_in_progress: 0,
                    test_pending: 0,
                    buildup_in_progress: 0,
                    buildup_pending: 0
                },
                pdi_flag_comparison: {
                    completed_count: 0,
                    in_progress_count: 0
                }
            });
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, []); // No dependencies - stable reference

    // SINGLE INITIALIZATION EFFECT - Check auth and load initial data
    useEffect(() => {
        // Prevent double execution in React StrictMode
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;

        const user = JSON.parse(localStorage.getItem("user"));
        if (user?.accessToken) {
            fetchDashboardData(reportPeriod);
        } else {
            navigate("/login");
        }

        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []); // Empty deps - run once only

    // Handle period changes separately from initial load
    useEffect(() => {
        // Skip initial mount (handled by effect above)
        if (!isInitializedRef.current) return;

        fetchDashboardData(reportPeriod);
    }, [reportPeriod, fetchDashboardData]);

    const handlePeriodChange = (e) => {
        const selectedPeriod = periodMapping[e.target.value] || 'weekly';
        setReportPeriod(selectedPeriod);
    };

    const getDisplayPeriod = () => {
        return reversePeriodMapping[reportPeriod] || 'Weekly';
    };

    // Calculate total count from complete_range data
    const getTotalCount = () => {
        if (!dashboardStats?.complete_range) return 0;

        return dashboardStats.complete_range.reduce((total, item) => {
            return total + (item.pdi || 0) + (item.buildup || 0) + (item.test || 0);
        }, 0);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <span>Loading dashboard...</span>
            </div>
        );
    }

    const progressStatus = calculateProgressStatus(dashboardStats);
    const readyToClose = calculateReadyToClose(dashboardStats);
    const { xAxisData, seriesData } = getChartData(dashboardStats);

    // Calculate totals for percentage calculation
    const progressTotal = progressStatus.inProgress + progressStatus.pending;
    const readyToCloseTotal = readyToClose.readyToClose + readyToClose.incomplete;

    // Prepare data for the pie charts with percentage labels
    const progressPieData = [
        {
            id: 0,
            value: progressStatus.inProgress,
            label: progressTotal > 0 ? `In Progress
 ${Math.round((progressStatus.inProgress / progressTotal) * 100)}%` : 'In Progress 0%',
            color: 'orange'
        },
        {
            id: 1,
            value: progressStatus.pending,
            label: progressTotal > 0 ? `Todo
 ${Math.round((progressStatus.pending / progressTotal) * 100)}%` : 'Todo 0%',
            color: 'red'
        }
    ];

    const readyToClosePieData = [
        {
            id: 0,
            value: readyToClose.readyToClose,
            label: readyToCloseTotal > 0 ? `Dr Approved
 ${Math.round((readyToClose.readyToClose / readyToCloseTotal) * 100)}%` : 'Dr Approved 0%',
            color: COLORS.primary.green
        },
        {
            id: 1,
            value: readyToClose.incomplete,
            label: readyToCloseTotal > 0 ? `Dr Request ${Math.round((readyToClose.incomplete / readyToCloseTotal) * 100)}%` : 'Dr Request 0%',
            color: COLORS.primary.blue
        }
    ];

    return (
        <div className="dashboard-container">
            {error && (
                <div className="error-banner">
                    {error}
                </div>
            )}

            <div className="dashboard-grid">
                {/* BOM Complete Report - Full Width */}
                <div className="dashboard-card full-width">
                    <div className="card-header">
                        <h3 className="card-title">Completed Report</h3>
                        <div className="header-info">
                                 Total Report: {getTotalCount()}
                             <select
                                value={getDisplayPeriod()}
                                onChange={handlePeriodChange}
                                className="period-select"
                            >
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Monthly">Monthly</option>
                            </select>
                        </div>
                    </div>

                    {/* Bar Chart with dynamic data based on period and API data */}
                    <div className="chart-container">
                        <ChartDataProvider
                            series={seriesData}
                            xAxis={[{
                                data: xAxisData,
                                scaleType: 'band',
                                id: 'x-axis'
                            }]}
                        >
                            <ChartsSurface width={700} height={200}>
                                <BarPlot
                                    borderRadius={4}
                                    slotProps={{
                                        bar: {
                                            style: {
                                                minWidth: '8px',
                                                maxWidth: '20px',
                                                rx: 4,
                                                ry: 4
                                            },
                                        },
                                    }}
                                />
                                <ChartsXAxis
                                    axisId="x-axis"
                                    slotProps={{
                                        axisLabel: {
                                            style: {
                                                fontSize: '12px',
                                                fill: COLORS.neutral[600]
                                            }
                                        }
                                    }}
                                />
                                <ChartsYAxis
                                    slotProps={{
                                        axisLabel: {
                                            style: {
                                                fontSize: '12px',
                                                fill: COLORS.neutral[600]
                                            }
                                        }
                                    }}
                                />
                                {/* Add tooltip component */}
                                <ChartsTooltip
                                    trigger="item"
                                    slotProps={{
                                        tooltip: {
                                            className: 'custom-tooltip',
                                            style: {
                                                backgroundColor: '#fff',
                                                border: `1px solid ${COLORS.neutral[200]}`,
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                padding: '12px',
                                                fontSize: '13px',
                                                color: COLORS.neutral[800]
                                            }
                                        },
                                    }}
                                />
                            </ChartsSurface>
                        </ChartDataProvider>
                        <div className="legend">

                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: COLORS.primary.yellow }}></div>
                                <span className="legend-text">{uploadModalProps['buildup']}</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: COLORS.primary.green }}></div>
                                <span className="legend-text">{uploadModalProps['test']}</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: COLORS.primary.blue }}></div>
                                <span className="legend-text">{uploadModalProps['pdi']}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress Status Card - Using PieChart with percentage labels */}
                <div className="dashboard-card-chart">
                    <h3 className="card-title">Progress Status</h3>
                    <div className="progress-container">
                        <div className="circular-progress-wrapper">
                            <PieChart
                                colors={[COLORS.primary.blue, COLORS.neutral[400]]}
                                series={[
                                    {
                                        data: progressPieData,
                                        outerRadius: 80,
                                        cornerRadius: 4,
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                        faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                        arcLabelMinAngle: 10,
                                        arcLabelRadius: 0.65
                                    }
                                ]}
                                width={200}
                                height={200}
                                slotProps={{
                                    legend: {
                                        hidden: true
                                    },
                                    tooltip: {
                                        trigger: 'item'
                                    }
                                }}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fill: 'white',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textShadow: '0px 0px 3px rgba(0,0,0,0.5)'
                                    },
                                }}
                            />
                        </div>
                    </div>

                </div>

                {/* Ready to Close Card - Using PieChart with percentage labels */}
                <div className="dashboard-card-chart">
                    <h3 className="card-title">Deviation Request</h3>
                    <div className="progress-container">
                        <div className="circular-progress-wrapper">
                            <PieChart
                                colors={[COLORS.primary.green, COLORS.primary.red]}
                                series={[
                                    {
                                        data: readyToClosePieData,
                                        outerRadius: 80,
                                        cornerRadius: 4,
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                        faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                        arcLabelMinAngle: 10,
                                        arcLabelRadius: 0.65
                                    }
                                ]}
                                width={200}
                                height={200}
                                slotProps={{
                                    legend: {
                                        hidden: true
                                    },
                                    tooltip: {
                                        trigger: 'item'
                                    }
                                }}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fill: 'white',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textShadow: '0px 0px 3px rgba(0,0,0,0.5)'
                                    },
                                }}
                            />
                        </div>

                    </div>

                </div>
            </div>
        </div>
    );
};

export default DashboardView;